/**
 * Helper de timezone para frontend-superadmin (Sprint 18 P27).
 *
 * Espejo de `apps/frontend-portal/src/lib/tiempo.ts`. Misma doctrina:
 * almacenar UTC, mostrar Santiago. Prohibido `toISOString().slice(0, 10)`
 * para representar "hoy" como día Santiago — usar `hoy()` de este módulo.
 *
 * Ver `apps/backend/app/utils/tiempo.py` para la versión backend
 * análoga.
 */

const APP_TZ = 'America/Santiago';


export function hoy(): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}


export function ahora(): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date());
}


export function formatearFecha(input: Date | string | null | undefined): string {
  if (!input) return '';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}


export function formatearFechaHora(input: Date | string | null | undefined): string {
  if (!input) return '';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}


// === Entrada de fecha dd-mm-aaaa (P27, ensayo LIVE 2026-06) ===========
//
// El <input type="date"> nativo guarda ISO yyyy-mm-dd, pero renderiza el
// picker según el locale del NAVEGADOR: en un browser en-US, tipear
// "01.06.2026" (1-jun) se interpreta mes-primero → 2026-01-06 (6-ene).
// La doctrina del proyecto es dd-mm-aaaa, NO estilo USA. `InputFecha` usa
// estos helpers para entrada locale-independiente: el usuario teclea
// dd-mm-aaaa y se almacena/emite ISO yyyy-mm-dd.

/** Máscara en vivo dd-mm-aaaa: deja solo dígitos (máx 8) e inserta guiones. */
export function formatearFechaEnVivo(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  const dd = d.slice(0, 2);
  const mm = d.slice(2, 4);
  const aaaa = d.slice(4, 8);
  let out = dd;
  if (d.length >= 3) out += `-${mm}`;
  if (d.length >= 5) out += `-${aaaa}`;
  return out;
}

/**
 * dd-mm-aaaa → ISO yyyy-mm-dd SOLO si es una fecha calendario real y
 * completa (rechaza 31-02-2026, etc.). Si no, retorna ''.
 */
export function displayFechaAIso(display: string): string {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(display.trim());
  if (!m) return '';
  const [, dd, mm, aaaa] = m;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(aaaa);
  // Piso/techo de sanidad: ninguna fecha del sistema (nacimientos de
  // personas vivas, tenencias, pagos, vencimientos, proyectos) cae fuera
  // de 1900-2100. Atrapa typos de año tipo "0206" → 206 d.C.
  if (year < 1900 || year > 2100) return '';
  const iso = `${aaaa}-${mm}-${dd}`;
  const dt = new Date(`${iso}T00:00:00`);
  if (
    Number.isNaN(dt.getTime()) ||
    dt.getDate() !== day ||
    dt.getMonth() + 1 !== month ||
    dt.getFullYear() !== year
  ) {
    return '';
  }
  return iso;
}

/** ISO yyyy-mm-dd (o ISO con tiempo) → dd-mm-aaaa para mostrar; '' si vacío. */
export function isoAFechaDisplay(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  const [, aaaa, mm, dd] = m;
  return `${dd}-${mm}-${aaaa}`;
}
